import React, { useMemo } from 'react';
import { Table, Typography, Image, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PurchaseInfo } from '../../../types/process/purchase';
import { useShopMap } from '../../../services/store/organization.store';
import { parseSkuNameParts } from '../../order/components/BusinessStyles';

const { Text } = Typography;

export type ShowField = 'base' | 'tab_quote_pending' | 'tab_delivery_pending' | 'tab_undeclared' | 'tab_declared' | 'tab_finished' | 'tab_financial_pending';

interface SceneConfig {
  columns: string[];
}

const SCENE_CONFIGS: Record<ShowField, SceneConfig> = {
  base: {
    columns: [
      'index',
      'purchaseNo',
      'orderNo',
      'storeInfo',
      'brand',
      'customerInfo',
      'productName',
      'sku',
      'tenancy',
    ],
  },
  tab_finished: {
    columns: [
      'index',
      'purchaseNo',
      'deviceImei',
      'orderNo',
      'storeInfo',
      'brand',
      'customerInfo',
      'productName',
      'sku',
      'tenancy',
      'supplierName',
      'applyTotal',
      'declareTime',
      'paymentType',
    ],
  },
  tab_quote_pending: {
    columns: [
      'index',
      'purchaseNo',
      'orderNo',
      'storeInfo',
      'brand',
      'customerInfo',
      'productName',
      'skuHighlight',
      'tenancy',
      'requirementTime',
      'pickupMethod',
      'lockStatus',
      'activationStatus',
      'requirementRemark',
    ],
  },
  tab_delivery_pending: {
    columns: [
      'index',
      'purchaseNo',
      'orderNo',
      'storeInfo',
      'brand',
      'customerInfo',
      'productName',
      'sku',
      'tenancy',
      'requirementTime',
      'pickupMethod',
      'lockStatus',
      'activationStatus',
      'quotedTime',
      'supplierName',
      'quoteInfo',
      'signatureUrl',
      'signatureTime',
    ],
  },
  tab_undeclared: {
    columns: [
      'index',
      'purchaseNo',
      'orderNo',
      'storeInfo',
      'brand',
      'customerInfo',
      'productName',
      'sku',
      'tenancy',
      'requirementTime',
      'pickupMethod',
      'lockStatus',
      'activationStatus',
      'quotedTime',
      'supplierName',
      'quoteInfo',
    ],
  },
  tab_declared: {
    columns: [
      'index',
      'purchaseNo',
      'orderNo',
      'storeInfo',
      'brand',
      'customerInfo',
      'productName',
      'sku',
      'tenancy',
      'supplierName',
      'deviceImei',
      'officialPrice',
      'accessoriesFee',
      'lockFee',
      'lockingFee',
      'taxAmount',
      'applyTotal',
      'purchaseVoucherUrl',
      'declareTime',
      'status',
    ],
  },
  tab_financial_pending: {
    columns: [
      'index',
      'purchaseNo',
      'orderNo',
      'deviceImei',
      'storeInfo',
      'brand',
      'customerInfo',
      'productName',
      'sku',
      'tenancy',
      'supplierName',
      'officialPrice',
      'accessoriesFee',
      'lockFee',
      'lockingFee',
      'taxAmount',
      'applyTotal',
      'purchaseVoucherUrl',
      'declareTime',
    ],
  },
};

export interface PurchaseSceneTableProps {
  dataSource: PurchaseInfo[];
  renderActions?: (record: PurchaseInfo) => React.ReactNode;
  showField?: ShowField;
  activeScene?: string;
  rowSelection?: any;
}

/** 格式化锁机状态 */
export const formatLockStatus = (lockStatus?: number): React.ReactNode => {
  if (lockStatus === 1) return <Text>有锁</Text>;
  if (lockStatus === 2) return <Text>无锁</Text>;
  return <Text type="secondary">未知</Text>;
};

/** 格式化激活状态 */
export const formatActivationStatus = (activationStatus?: number): React.ReactNode => {
  if (activationStatus === 1) return <Text>是</Text>;
  if (activationStatus === 2) return <Text>否</Text>;
  return <Text type="secondary">未知</Text>;
};

/** 格式化租期显示 */
const formatTenancy = (
  tenancyData?: { start_time?: string; end_time?: string; total_days?: number } | null
): React.ReactNode => {
  if (!tenancyData) return <Text type="secondary">—</Text>;
  const { start_time, end_time, total_days } = tenancyData;
  if (!start_time && !end_time) return <Text type="secondary">—</Text>;
  const start = start_time ? start_time.slice(0, 10) : '?';
  const end = end_time ? end_time.slice(0, 10) : '?';
  return (
    <div>
      <div>{start} ~ {end}</div>
      {total_days ? <Text type="secondary">共{total_days}天</Text> : null}
    </div>
  );
};

/** 格式化日期时间 */
const formatDateTime = (val?: string | null): string => {
  if (!val) return '—';
  return new Date(val).toLocaleString('zh-CN', { hour12: false });
};

/** 格式化金额 */
const formatMoney = (val?: number | null): React.ReactNode => {
  if (val === undefined || val === null) return <Text type="secondary">—</Text>;
  return <Text>{Number(val).toFixed(2)}</Text>;
};

const PurchaseSceneTable: React.FC<PurchaseSceneTableProps> = ({
  dataSource,
  renderActions,
  showField = 'base',
  rowSelection,
}) => {
  const { shopMap } = useShopMap();
  const currentConfig = SCENE_CONFIGS[showField] || SCENE_CONFIGS.base;

  const columns: ColumnsType<PurchaseInfo> = useMemo(() => {
    const columnMap: Record<string, any> = {
      /** 序号 */
      index: {
        title: '序号',
        key: 'index',
        width: 60,
        render: (_: any, __: any, rowIndex: number) => rowIndex + 1,
      },

      /** 采购单号 */
      purchaseNo: {
        title: '采购单号',
        key: 'purchaseNo',
        width: 140,
        render: (_: any, record: PurchaseInfo) => (
          <div>
            <Text copyable style={{ fontSize: 13 }}>{record.purchase.purchaseNo}</Text>
            {record.purchase.source === 1 && (
              <div style={{ marginTop: 4 }}>
                <span style={{ 
                  display: 'inline-block', 
                  backgroundColor: '#fffbe6', 
                  color: '#faad14', 
                  border: '1px solid #ffe58f', 
                  padding: '0 6px', 
                  borderRadius: '10px', 
                  fontSize: '12px' 
                }}>已退回</span>
              </div>
            )}
          </div>
        ),
      },

      /** 订单号 */
      orderNo: {
        title: '订单号',
        key: 'orderNo',
        width: 140,
        render: (_: any, record: PurchaseInfo) => (
          <div><Text copyable style={{ fontSize: 13 }}>{record.purchase.orderNo}</Text></div>
        ),
      },

      /** 审批编号 */
      approvalNo: {
        title: '审批编号',
        key: 'approvalNo',
        width: 140,
        render: (_: any, record: PurchaseInfo) => (
          <div><Text copyable style={{ fontSize: 13 }}>{record.purchase.wechatApprovalNo || '-'}</Text></div>
        ),
      },

      /** 店铺 */
      storeInfo: {
        title: '店铺',
        key: 'storeInfo',
        width: 50,
        render: (_: any, record: PurchaseInfo) => {
          const storeId = record.purchase.storeId;
          const shop = storeId ? shopMap.get(storeId as number) : undefined;
          return storeId ? <Text strong>{shop?.name || String(storeId)}</Text> : <Text type="secondary">—</Text>;
        },
      },

      /** 品牌 */
      brand: {
        title: '品牌',
        key: 'brand',
        width: 60,
        render: (_: any, record: PurchaseInfo) => {
          const brand = record.order?.productBrand || record.purchase.brand;
          return brand ? <Text>{brand}</Text> : <Text type="secondary">—</Text>;
        },
      },

      /** 客户信息：姓名 + 手机号 */
      customerInfo: {
        title: '客户信息',
        key: 'customerInfo',
        width: 110,
        render: (_: any, record: PurchaseInfo) => {
          const name = record.order?.userName;
          const phone = record.order?.userPhone;
          if (!name && !phone) return <Text type="secondary">—</Text>;
          return (
            <div>
              {name && <div><Text strong>{name}</Text></div>}
              {phone && <div><Text type="secondary">{phone}</Text></div>}
            </div>
          );
        },
      },

      /** 商品名称 */
      productName: {
        title: '商品名称',
        key: 'productName',
        width: 140,
        render: (_: any, record: PurchaseInfo) => {
          const title = record.order?.productTitle || record.orderData?.productTitle;
          return title ? <Text>{title}</Text> : <Text type="secondary">—</Text>;
        },
      },

      /** 套餐（SKU） */
      sku: {
        title: '套餐',
        key: 'sku',
        width: 120,
        render: (_: any, record: PurchaseInfo) => {
          const sku = record.orderData?.skuName || record.order?.productSku;
          if (!sku) return <Text type="secondary">—</Text>;
          const parsedParams = parseSkuNameParts(sku);
          return (
            <Space direction="vertical" size={2}>
              <Text>{parsedParams.packageName}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[parsedParams.color, parsedParams.memory].filter(Boolean).join(' ')}
              </Text>
            </Space>
          );
        },
      },

      /** 套餐 (高亮) */
      skuHighlight: {
        title: '套餐',
        key: 'skuHighlight',
        width: 140,
        render: (_: any, record: PurchaseInfo) => {
          const sku = record.orderData?.skuName || record.order?.productSku;
          if (!sku) return <Text type="secondary">—</Text>;
          const parsedParams = parseSkuNameParts(sku);
          return (
            <Space direction="vertical" size={2}>
              <Text>{parsedParams.packageName}</Text>
              <Text style={{ fontSize: 12, color: '#000' }}>
                {[
                  parsedParams.color ? <span key="color" style={{ color: '#ef4444', fontWeight: 'bold' }}>{parsedParams.color}</span> : null,
                  parsedParams.memory ? <span key="memory" style={{ color: '#ef4444', fontWeight: 'bold' }}>{parsedParams.memory}</span> : null
                ].filter(Boolean).reduce((prev, curr) => prev === null ? [curr] : [prev, ' ', curr] as any, null)}
              </Text>
            </Space>
          );
        },
      },

      /** 租期：来自 orderData.tenancyData */
      tenancy: {
        title: '租期',
        key: 'tenancy',
        width: 150,
        render: (_: any, record: PurchaseInfo) =>
          formatTenancy(record.orderData?.tenancyData),
      },

      /** 需求提交时间 */
      requirementTime: {
        title: '需求提交时间',
        key: 'requirementTime',
        width: 140,
        render: (_: any, record: PurchaseInfo) =>
          formatDateTime((record.purchase as any).createdAt),
      },

      /** 揽收时间（原样展示 pickupMethod） */
      pickupMethod: {
        title: '揽收时间',
        key: 'pickupMethod',
        width: 140,
        render: (_: any, record: PurchaseInfo) =>
          record.purchase.pickupMethod ? <Text>{record.purchase.pickupMethod}</Text> : <Text type="secondary">—</Text>,
      },

      /** 是否有锁 */
      lockStatus: {
        title: '是否有锁',
        key: 'lockStatus',
        width: 90,
        render: (_: any, record: PurchaseInfo) =>
          formatLockStatus(record.purchase.lockStatus),
      },

      /** 是否激活 */
      activationStatus: {
        title: '是否激活',
        key: 'activationStatus',
        width: 90,
        render: (_: any, record: PurchaseInfo) =>
          formatActivationStatus(record.purchase.activationStatus),
      },

      /** 其他需求说明 */
      requirementRemark: {
        title: '其他需求说明',
        key: 'requirementRemark',
        width: 180,
        render: (_: any, record: PurchaseInfo) =>
          record.purchase.requirementRemark
            ? <Text>{record.purchase.requirementRemark}</Text>
            : <Text type="secondary">—</Text>,
      },

      /** 报价提交时间 */
      quotedTime: {
        title: '报价提交时间',
        key: 'quotedTime',
        width: 140,
        render: (_: any, record: PurchaseInfo) =>
          formatDateTime(record.purchase.quotedTime),
      },

      /** 供应商 */
      supplierName: {
        title: '供应商',
        key: 'supplierName',
        width: 120,
        render: (_: any, record: PurchaseInfo) =>
          record.purchase.supplierName
            ? <Text>{record.purchase.supplierName}</Text>
            : <Text type="secondary">—</Text>,
      },

      /** 报价信息 */
      quoteInfo: {
        title: '报价信息',
        key: 'quoteInfo',
        width: 180,
        render: (_: any, record: PurchaseInfo) => {
          const { quotedPrice, activationDate } = record.purchase;
          if (!quotedPrice) return <Text type="secondary">—</Text>;

          const dateStr = activationDate ? formatDateTime(activationDate).split(' ')[0] : null;
          return (
            <div style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', padding: '4px 8px', borderRadius: '4px', display: 'inline-block' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: dateStr ? 4 : 0 }}>
                <Text strong>{quotedPrice}元</Text>
                <span style={{ marginLeft: 8 }}>
                  {activationDate ? <Text>已激活</Text> : <Text>未激活</Text>}
                </span>
              </div>
              {dateStr && <div><Text type="secondary" style={{ fontSize: 12 }}>{dateStr}</Text></div>}
            </div>
          );
        },
      },

      /** 微签URL */
      signatureUrl: {
        title: '微签URL',
        key: 'signatureUrl',
        width: 100,
        render: (_: any, record: PurchaseInfo) => {
          const url = record.purchase.signatureUrl;
          return url ? (
            <Image src={url} width={50} height={50} style={{ objectFit: 'contain' }} />
          ) : (
            <Text type="secondary">—</Text>
          );
        },
      },

      /** 微签生成时间 */
      signatureTime: {
        title: '微签生成时间',
        key: 'signatureTime',
        width: 140,
        render: (_: any, record: PurchaseInfo) =>
          formatDateTime(record.purchase.signatureTime),
      },

      /** 设备IMEI串码 */
      deviceImei: {
        title: '设备串码',
        key: 'deviceImei',
        width: 150,
        render: (_: any, record: PurchaseInfo) =>
          record.purchase.deviceImei ? <Text>{record.purchase.deviceImei}</Text> : <Text type="secondary">—</Text>,
      },

      /** 官网价 */
      officialPrice: {
        title: '官网价',
        key: 'officialPrice',
        width: 100,
        render: (_: any, record: PurchaseInfo) => {
          const val = record.orderData?.productValue;
          return val != null ? <Text>{Number(val).toFixed(2)}</Text> : <Text type="secondary">—</Text>;
        },
      },

      /** 配件费 */
      accessoriesFee: {
        title: '配件费',
        key: 'accessoriesFee',
        width: 100,
        render: (_: any, record: PurchaseInfo) => formatMoney(record.purchase.accessoriesFee),
      },

      /** 锁费 */
      lockFee: {
        title: '锁费',
        key: 'lockFee',
        width: 100,
        render: (_: any, record: PurchaseInfo) => formatMoney(record.purchase.lockFee),
      },

      /** 上锁费 */
      lockingFee: {
        title: '上锁费',
        key: 'lockingFee',
        width: 100,
        render: (_: any, record: PurchaseInfo) => formatMoney(record.purchase.lockingFee),
      },

      /** 税点额 */
      taxAmount: {
        title: '税点额',
        key: 'taxAmount',
        width: 100,
        render: (_: any, record: PurchaseInfo) => formatMoney(record.purchase.taxAmount),
      },

      /** 申请合计 */
      applyTotal: {
        title: '申请合计',
        key: 'applyTotal',
        width: 100,
        render: (_: any, record: PurchaseInfo) => formatMoney(record.purchase.applyTotal),
      },

      /** 支付方式 */
      paymentType: {
        title: '支付方式',
        key: 'paymentType',
        width: 100,
        render: (_: any, record: PurchaseInfo) =>
          record.purchase.paymentType ? <Text>{record.purchase.paymentType}</Text> : <Text type="secondary">—</Text>,
      },

      /** 采购截图凭证URL */
      purchaseVoucherUrl: {
        title: '采购截图凭证',
        key: 'purchaseVoucherUrl',
        width: 120,
        render: (_: any, record: PurchaseInfo) => {
          const url = record.purchase.purchaseVoucherUrl;
          return url ? (
             <Image src={url} width={50} height={50} style={{ objectFit: 'contain' }} />
          ) : (
            <Text type="secondary">—</Text>
          );
        },
      },

      /** 申报时间 */
      declareTime: {
        title: '申报时间',
        key: 'declareTime',
        width: 140,
        render: (_: any, record: PurchaseInfo) =>
          formatDateTime(record.purchase.declareTime),
      },

      /** 状态 */
      status: {
        title: '状态',
        key: 'status',
        width: 100,
        render: (_: any, record: PurchaseInfo) => {
          switch (record.purchase.status) {
            case 1: return <Text>待报价</Text>;
            case 5: return <Text>待申报</Text>;
            case 11: return <Text style={{ color: '#1890ff' }}>已申报</Text>;
            case 16: return <Text style={{ color: '#52c41a' }}>已完成</Text>;
            case 21: return <Text style={{ color: '#f5222d' }}>已退回</Text>;
            default: return <Text type="secondary">—</Text>;
          }
        },
      },

      /** 操作列 */
      action: {
        title: '操作',
        key: 'action',
        fixed: 'right' as const,
        width: 140,
        render: (_: any, record: PurchaseInfo) =>
          renderActions ? renderActions(record) : null,
      },
    };

    const finalColumns: ColumnsType<PurchaseInfo> = currentConfig.columns
      .map((key) => columnMap[key])
      .filter(Boolean);

    finalColumns.forEach((col, index) => {
      if (index < 6) {
        col.fixed = 'left';
      }
    });

    if (renderActions) {
      finalColumns.push(columnMap.action);
    }

    return finalColumns;
  }, [renderActions, currentConfig, shopMap]);

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={columns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        rowKey={(record) => record.purchase.purchaseNo}
        scroll={{ x: 'max-content' }}
        pagination={false}
        size="small"
      />
    </div>
  );
};

export default PurchaseSceneTable;
